import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { Content } from "@/components/layouts/content";
import { LeadForm } from "@/components/leads/lead-form";
import { LeadStatusManager } from "@/components/leads/lead-status-manager";
import {
	leadCategoriesQueryOptions,
	leadStatusesQueryOptions,
	leadsQueryOptions,
	useLeadCreateMutation,
	useLeadDeleteMutation,
	useLeadUpdateMutation,
} from "@/features/dashboard/api/queries/lead-query";
import type { Lead } from "@/features/dashboard/types/lead";
import type { CreateLeadData, LeadStatus } from "@/features/dashboard/types/lead";
import { queryClient } from "@/query-client";
import { formatNumber } from "@/utils/format-number";
import * as Dialog from "@radix-ui/react-dialog";
import { IconDownload, IconPlus, IconSearch, IconUpload, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const PRIORITY_COLOR_MAP: Record<string, string> = {
	high: "bg-red-100 text-red-800",
	medium: "bg-yellow-100 text-yellow-800",
	low: "bg-green-100 text-green-800",
};

const STATUS_COLOR_MAP: Record<string, string> = {
	green: "bg-green-100 text-green-800",
	yellow: "bg-yellow-100 text-yellow-800",
	red: "bg-red-100 text-red-800",
	blue: "bg-blue-100 text-blue-800",
	purple: "bg-purple-100 text-purple-800",
	gray: "bg-gray-100 text-gray-800",
	orange: "bg-orange-100 text-orange-800",
};

function buildColumns(statuses: LeadStatus[]): ColumnDef<Lead>[] {
	const statusLabels = Object.fromEntries(statuses.map((s) => [s.slug, s.name]));
	const statusColors = Object.fromEntries(statuses.map((s) => [s.slug, STATUS_COLOR_MAP[s.color] || "bg-gray-100 text-gray-800"]));

	return [
		{
			id: "reference_number",
			label: "Ref No.",
			cellClassName: "min-w-[120px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => (
				<Link to="/leads/$leadId" params={{ leadId: data.id || "" }} className="font-semibold text-blue-600 hover:underline">
					{data.reference_number}
				</Link>
			),
		},
		{
			id: "proposal_date",
			label: "Proposal Date",
			cellClassName: "min-w-[110px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="text-gray-900">{data.proposal_date ? new Date(data.proposal_date).toLocaleDateString() : "-"}</span>,
		},
		{
			id: "project_description",
			label: "Project Description",
			cellClassName: "min-w-[200px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="text-gray-900 text-sm">{data.project_description}</span>,
		},
		{
			id: "client_name",
			label: "Client Name",
			cellClassName: "min-w-[150px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="font-medium text-gray-900">{data.client_name}</span>,
		},
		{
			id: "amount",
			label: "Amount",
			cellClassName: "min-w-[120px] text-right",
			headerClassName: "whitespace-nowrap text-right",
			cell: ({ data }) => <span className="font-medium text-gray-900">&#8369;{formatNumber(data.amount)}</span>,
		},
		{
			id: "contact_person",
			label: "Contact Person",
			cellClassName: "min-w-[130px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="text-gray-900 text-sm">{data.contact_person || "-"}</span>,
		},
		{
			id: "prepared_by_user",
			label: "Prepared By",
			cellClassName: "min-w-[130px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="text-gray-900 text-sm">{data.prepared_by_user?.name || "-"}</span>,
		},
		{
			id: "priority",
			label: "Priority",
			cellClassName: "min-w-[100px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) =>
				data.priority ? (
					<span className={`px-2 py-1 rounded-full text-xs font-medium ${PRIORITY_COLOR_MAP[data.priority] || "bg-gray-100 text-gray-800"}`}>
						{data.priority.charAt(0).toUpperCase() + data.priority.slice(1)}
					</span>
				) : (
					<span className="text-gray-400 text-sm">-</span>
				),
		},
		{
			id: "status",
			label: "Status",
			cellClassName: "min-w-[120px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => (
				<span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[data.status] || "bg-gray-100 text-gray-800"}`}>
					{statusLabels[data.status] || data.status}
				</span>
			),
		},
		{
			id: "remarks",
			label: "Remarks",
			cellClassName: "min-w-[150px]",
			headerClassName: "whitespace-nowrap",
			cell: ({ data }) => <span className="text-gray-900 text-sm">{data.remarks || "-"}</span>,
		},
	];
}

export const Route = createFileRoute("/_dashboard/leads")({
	validateSearch: z.object({
		"filter[search]": z.string().optional(),
		"filter[status]": z.string().optional(),
		"filter[category]": z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().nullable().optional(),
	}),
	component: LeadsPage,
});

function LeadsPage() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const [searchTerm, setSearchTerm] = useState(search["filter[search]"] || "");
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [editingLead, setEditingLead] = useState<Lead | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const deleteLeadMutation = useLeadDeleteMutation();

	const statusesQuery = useQuery(leadStatusesQueryOptions());
	const statuses = statusesQuery.data || [];
	const columns = buildColumns(statuses);

	const categoriesQuery = useQuery(leadCategoriesQueryOptions());
	const categories = categoriesQuery.data || [];

	const leadsQuery = useQuery(
		leadsQueryOptions({
			...search,
		}),
	);

	const handleSearch = async () => {
		await navigate({
			to: "/leads",
			search: (old) => ({
				...old,
				"filter[search]": searchTerm || undefined,
				page: 1,
			}),
		});
	};

	const handleClearSearch = async () => {
		setSearchTerm("");
		await navigate({
			to: "/leads",
			search: (old) => ({
				...old,
				"filter[search]": undefined,
				page: 1,
			}),
		});
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};

	const handleCategoryTab = async (category: string | undefined) => {
		await navigate({
			to: "/leads",
			search: (old) => ({
				...old,
				"filter[category]": category,
				page: 1,
			}),
		});
	};

	const handleStatusFilter = async (status: string | undefined) => {
		await navigate({
			to: "/leads",
			search: (old) => ({
				...old,
				"filter[status]": status,
				page: 1,
			}),
		});
	};

	const handleExportExcel = () => {
		const params = new URLSearchParams();
		if (search["filter[search]"]) params.append("filter[search]", search["filter[search]"]);
		if (search["filter[status]"]) params.append("filter[status]", search["filter[status]"]);
		if (search["filter[category]"]) params.append("filter[category]", search["filter[category]"]);
		window.open(`/api/leads/export/excel?${params.toString()}`, "_blank");
	};

	const handleDeleteLead = async (lead: Lead) => {
		if (lead.id) {
			await deleteLeadMutation.mutateAsync(lead.id);
			await queryClient.invalidateQueries({ queryKey: ["leads"] });
			toast.success("Lead deleted successfully");
		}
	};

	const leads = leadsQuery.data?.data || [];
	const meta = leadsQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 15,
		status_count: {},
	};

	if (leadsQuery.isLoading) {
		return (
			<Content pageName="Leads">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading leads...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Leads">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Leads</span>
						<span className="text-base text-foreground/70">Track sales proposals and leads</span>
					</div>
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => navigate({ to: "/leads/import" })}>
							<IconUpload size={18} stroke={2} />
							<span>Import Excel</span>
						</Button>
						<Button variant="outline" onClick={handleExportExcel}>
							<IconDownload size={18} stroke={2} />
							<span>Export Excel</span>
						</Button>
						<Button onClick={() => setIsCreateModalOpen(true)}>
							<IconPlus size={18} stroke={2} />
							<span>Add New</span>
						</Button>
					</div>
				</div>

				{/* Search & Status Filter */}
				<div className="flex items-center gap-2 w-full">
					<div className="relative flex-1 max-w-md">
						<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
						<input
							type="text"
							placeholder="Search leads..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyDown={handleKeyPress}
							className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
						/>
						{searchTerm && (
							<button
								type="button"
								onClick={handleClearSearch}
								className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
							>
								<IconX size={16} />
							</button>
						)}
					</div>
					<Button onClick={handleSearch} variant="outline">
						Search
					</Button>
					<div className="flex items-center gap-1 ml-4">
						<button
							type="button"
							onClick={() => handleStatusFilter(undefined)}
							className={clsx(
								"px-3 py-1.5 text-xs font-medium rounded-full transition-colors",
								!search["filter[status]"] ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200",
							)}
						>
							All
						</button>
						{statuses.map((s) => (
							<button
								key={s.slug}
								type="button"
								onClick={() => handleStatusFilter(s.slug)}
								className={clsx(
									"px-3 py-1.5 text-xs font-medium rounded-full transition-colors",
									search["filter[status]"] === s.slug ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200",
								)}
							>
								{s.name}
							</button>
						))}
						<LeadStatusManager />
					</div>
				</div>

				{/* Category Tabs */}
				{categories.length > 0 && (
					<div className="flex items-center gap-1 overflow-x-auto border-b border-border/50 pb-px">
						<button
							type="button"
							onClick={() => handleCategoryTab(undefined)}
							className={clsx(
								"px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-md border border-b-0 transition-colors",
								!search["filter[category]"]
									? "bg-white text-primary-700 border-border/50 -mb-px"
									: "bg-gray-50 text-foreground/60 border-transparent hover:bg-gray-100 hover:text-foreground",
							)}
						>
							All
						</button>
						{categories.map((cat) => (
							<button
								key={cat}
								type="button"
								onClick={() => handleCategoryTab(cat)}
								className={clsx(
									"px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-md border border-b-0 transition-colors",
									search["filter[category]"] === cat
										? "bg-white text-primary-700 border-border/50 -mb-px"
										: "bg-gray-50 text-foreground/60 border-transparent hover:bg-gray-100 hover:text-foreground",
								)}
							>
								{cat}
							</button>
						))}
					</div>
				)}

				<div className="flex flex-col w-full gap-4">
					<DataTable
						className="border-collapse"
						data={leads}
						columns={columns}
						stickyHead={false}
						stickyCol={false}
						showBorder={true}
						compact={true}
						showToolbar={false}
						onEdit={(lead) => {
							setEditingLead(lead);
							setIsEditModalOpen(true);
						}}
						onDelete={handleDeleteLead}
					/>
					<Pagination
						onClick={async (page) => {
							await navigate({
								to: "/leads",
								search: (old) => ({
									...old,
									page,
								}),
							});
						}}
						initialPage={meta.current_page}
						pageCount={meta.last_page}
					/>
				</div>
			</div>

			{/* Create Modal */}
			<CreateLeadModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={() => leadsQuery.refetch()} />

			{/* Edit Modal */}
			{editingLead && (
				<EditLeadModal
					lead={editingLead}
					isOpen={isEditModalOpen}
					onClose={() => {
						setIsEditModalOpen(false);
						setEditingLead(null);
					}}
					onSuccess={() => leadsQuery.refetch()}
				/>
			)}
		</Content>
	);
}

function CreateLeadModal({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void; onSuccess: () => void }) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const createMutation = useLeadCreateMutation();

	const handleSubmit = async (values: CreateLeadData) => {
		setIsSubmitting(true);
		createMutation.mutate(values, {
			onSuccess: () => {
				toast.success("Lead created successfully");
				onSuccess();
				onClose();
				setIsSubmitting(false);
			},
			onError: () => {
				setIsSubmitting(false);
			},
		});
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">Create New Lead</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<LeadForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="lead-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#lead-form") as HTMLFormElement;
								if (form) form.requestSubmit();
							}}
						>
							{isSubmitting ? "Creating..." : "Create Lead"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function EditLeadModal({ lead, isOpen, onClose, onSuccess }: { lead: Lead; isOpen: boolean; onClose: () => void; onSuccess: () => void }) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const updateMutation = useLeadUpdateMutation(lead.id || "");

	const handleSubmit = async (values: CreateLeadData) => {
		setIsSubmitting(true);
		updateMutation.mutate(
			{ ...values, id: lead.id || "" },
			{
				onSuccess: () => {
					toast.success("Lead updated successfully");
					onSuccess();
					onClose();
					setIsSubmitting(false);
				},
				onError: () => {
					setIsSubmitting(false);
				},
			},
		);
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">Edit Lead</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<LeadForm lead={lead} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="lead-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#lead-form") as HTMLFormElement;
								if (form) form.requestSubmit();
							}}
						>
							{isSubmitting ? "Updating..." : "Update Lead"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
